import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

const PrincipalSwapModal = () => {
    const { isOpen, id } = useSelector((state) => state.application.principalSwapModal);
    const { dispatch } = store;
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);

    const [application, set_application] = useState(null);
    const [principalId, set_principalId] = useState(0);
    const [principal, set_principal] = useState(null);
    const [loadingMessage, set_loadingMessage] = useState('');

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        noPaddingRight: { paddingLeft: '0' },
        padding10Bottom: { paddingBottom: '10px' },
        padding20Bottom: { paddingBottom: '20px' },
        nonEditable: { padding: '5px 10px' },
        buttonContainer: { marginRight: '20px', textAlign: 'right' },
        principal: {
            border: '1px solid silver',
            borderRadius: '5px',
            padding: '10px',
        },
        changedPrincipal: {
            border: '1px solid silver',
            borderRadius: '5px',
            padding: '10px',
            backgroundColor: 'aliceblue',
        },
        warning: { color: 'darkred' },
    };

    useEffect(() => {
        if (!id) {
            resetState();
            return;
        }

        const loadAppAsync = async () => {
            await loadApplication(id);
        };
        loadAppAsync();
    }, [id]);

    const resetState = () => {
        set_application(null);
        set_principalId(0);
        set_principal(null);
    };

    const _handleClose = () => {
        dispatch(
            applicationActions.setPrincipalSwapModal({
                principalSwapModal: { isOpen: false },
            })
        );
    };

    const loadApplication = async (id) => {
        try {
            set_loadingMessage('Loading...');
            const app = (await applicationApi.getPrincipalSwap(id)).result;
            if (!app) {
                return;
            }
            set_application(app);
            set_principalId(app.principalId);
        } finally {
            set_loadingMessage('');
        }
    };

    const updatePrincipal = async (e) => {
        e.stopPropagation();
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');

            await applicationApi.setPrincipalSwap(application.id, principalId);

            handleAdminSuccess('Changes saved successfully!');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const onPrincipalChanged = useCallback((id) => {
        set_principalId(id);
    }, []);

    useEffect(() => {
        if (!principalId) {
            set_principal(null);
            return;
        }

        const princ = application.principals.find((p) => p.id === principalId);
        set_principal(princ);
    }, [principalId]);

    if (!application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '850px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div>
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>Application Principal Swap</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <div style={styles.nonEditable}>
                                        <h2 className='decorated'>Borrower Details</h2>
                                        <div
                                            className='row'
                                            style={styles.padding10Bottom}
                                        >
                                            <div className='columns small-12 large-12'>
                                                <b>{application.entityName}</b>
                                                {(application.street1 || application.street2) && (
                                                    <>
                                                        <br /> {application.street1} {application.street2}
                                                    </>
                                                )}{' '}
                                                {(application.city || application.state || application.zipCode) && (
                                                    <>
                                                        <br /> {application.city}, {application.state} {application.zipCode}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className='row'
                                            style={styles.padding20Bottom}
                                        >
                                            <div className='columns small-12 large-12'>
                                                <label htmlFor='principalId'>Select Principal:</label>
                                                {application.principals.length <= 1 && (
                                                    <div style={styles.warning}>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} /> There are no alternate
                                                        principals associated with the Entity that are available for selection!
                                                    </div>
                                                )}
                                                <select
                                                    id='principalSelect'
                                                    value={principalId}
                                                    onChange={(e) => onPrincipalChanged(parseInt(e.target.value))}
                                                    required={true}
                                                    disabled={principalId && application.principals.length <= 1}
                                                >
                                                    {application.principals.map((p) => {
                                                        return (
                                                            <option
                                                                ke={p.id}
                                                                value={p.id}
                                                            >
                                                                {p.name} {p.email ? '(' + p.email + ')' : ''}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {principal && (
                                                    <div
                                                        style={
                                                            principalId === application.principalId
                                                                ? styles.principal
                                                                : styles.changedPrincipal
                                                        }
                                                    >
                                                        <div>
                                                            <b>{principal.name}</b>
                                                        </div>
                                                        <div>Officer: {principal.isOfficer ? 'Yes' : 'No'}</div>
                                                        <div>% Owned: {principal.percentOwnership ?? 'Not Specified'}</div>
                                                        {principal.email && (
                                                            <div>
                                                                <a href='mailto:{principal.email}'>{principal.email}</a>
                                                            </div>
                                                        )}
                                                        {principal.street && <div>{principal.street} </div>}
                                                        {(principal.city || principal.state || principal.zipCode) && (
                                                            <div>
                                                                {principal.city} {principal.state}, {principal.zipCode}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <h2 className='decorated'>Loan Type & BFI #</h2>
                                        <div
                                            className='row'
                                            style={styles.padding10Bottom}
                                        >
                                            <div className='columns small-12 large-4'>{application?.loanDescription}</div>
                                            {application?.bfiNumber && (
                                                <div className='columns small-12 large-8'>{application?.bfiNumber}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div className='columns small-12 large-12'>
                                            <h2 className='decorated'>Properties</h2>
                                            <ul>
                                                {(!application.properties || application.properties.length === 0) && (
                                                    <li>No Properties found!</li>
                                                )}
                                                {application.properties &&
                                                    application.properties.length > 0 &&
                                                    application.properties.map((p) => {
                                                        return (
                                                            <li key={'p' + p.id}>
                                                                {p.address?.street1} {p.address?.street2}, {p.address?.city}{' '}
                                                                {p.address?.state}, {p.address?.zipCode}
                                                            </li>
                                                        );
                                                    })}
                                            </ul>
                                        </div>
                                    </div>
                                    <form
                                        name='principalSwapForm'
                                        onSubmit={(event) => updatePrincipal(event)}
                                    >
                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                style={styles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                                title='Save changes and close modal'
                                                disabled={principalId === application.principalId}
                                            >
                                                Save
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default PrincipalSwapModal;
